import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, MapPin, Bed, Bath, Square, Upload,
  ChevronLeft, ChevronRight, CheckCircle2, Image, FileText,
  BarChart3, AlertTriangle, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AEDIcon from '@/components/icons/AEDIcon';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import api from '@/services/api';

const STEPS = [
  { id: 'details', label: 'Property Details', icon: Building2 },
  { id: 'ownership', label: 'Ownership', icon: FileText },
  { id: 'media', label: 'Photos', icon: Image },
  { id: 'pricing', label: 'Pricing Analytics', icon: BarChart3 },
  { id: 'review', label: 'Submit', icon: CheckCircle2 }
];

const PROPERTY_TYPES = [
  { value: 'apartment', label: 'Apartment' },
  { value: 'villa', label: 'Villa' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'penthouse', label: 'Penthouse' },
  { value: 'studio', label: 'Studio' },
  { value: 'duplex', label: 'Duplex' },
  { value: 'land', label: 'Land' },
  { value: 'retail', label: 'Retail' },
  { value: 'office', label: 'Office' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'commercial', label: 'Commercial' }
];

// Complete UAE Cities and Areas - Cascading data (#20-24)
const CITIES_AREAS = {
  Dubai: [
    'Downtown Dubai', 'Dubai Marina', 'Palm Jumeirah', 'Business Bay', 'JBR', 'JVC (Jumeirah Village Circle)',
    'JVT (Jumeirah Village Triangle)', 'DIFC', 'City Walk', 'Dubai Hills Estate', 'Emirates Hills',
    'Arabian Ranches', 'Arabian Ranches 2', 'Arabian Ranches 3', 'Al Barsha', 'Al Barsha South',
    'Al Quoz', 'Jumeirah', 'Jumeirah Islands', 'Jumeirah Golf Estates', 'Jumeirah Lake Towers',
    'Jumeirah Park', 'Umm Suqeim', 'The Springs', 'The Meadows', 'The Lakes', 'The Greens',
    'The Views', 'Motor City', 'Sports City', 'Dubai Silicon Oasis', 'Dubai Investment Park',
    'Dubai South', 'Dubai Creek Harbour', 'MBR City', 'Meydan', 'Damac Hills', 'Damac Hills 2',
    'Town Square', 'Mudon', 'Remraam', 'Mirdif', 'Al Rashidiya', 'Al Warqa', 'International City',
    'Discovery Gardens', 'Al Furjan', 'Tilal Al Ghaf', 'Bluewaters Island', 'La Mer', 'Pearl Jumeirah',
    'Madinat Jumeirah Living', 'Port de La Mer', 'Emaar Beachfront', 'Dubai Harbour', 'Marsa Al Arab',
    'Sobha Hartland', 'Villanova', 'Dubai Festival City', 'Al Jaddaf', 'Culture Village', 'Deira',
    'Bur Dubai', 'Karama', 'Satwa', 'Oud Metha', 'Healthcare City', 'Academic City', 'Studio City'
  ],
  'Abu Dhabi': [
    'Saadiyat Island', 'Yas Island', 'Al Reem Island', 'Al Maryah Island', 'Al Raha Beach',
    'Al Reef', 'Al Ghadeer', 'Masdar City', 'Khalifa City A', 'Khalifa City B', 'Mohamed Bin Zayed City',
    'Al Shamkha', 'Shakhbout City', 'Baniyas', 'Al Wathba', 'Al Falah', 'Hydra Village',
    'Al Samha', 'Al Bahia', 'Al Rahba', 'Corniche Area', 'Al Khalidiyah', 'Al Bateen',
    'Tourist Club Area', 'Al Markaziyah', 'Al Mushrif', 'Al Nahyan', 'Al Manaseer', 'Al Muroor',
    'Rawdhat Abu Dhabi', 'Al Musalla', 'Al Maqtaa', 'Officers City', 'Zayed Sports City',
    'Al Raha Gardens', 'Al Reef Villas', 'Al Reef Downtown', 'Bloom Gardens', 'Faya',
    'Yas Acres', 'West Yas', 'Nurai Island', 'Jubail Island', 'Hudayriyat Island',
    'Mamsha Al Saadiyat', 'Louvre Abu Dhabi District', 'Marina Village', 'Al Zeina', 'Al Muneera'
  ],
  Sharjah: [
    'Al Majaz', 'Al Nahda', 'Al Khan', 'Al Qasba', 'Al Taawun', 'Al Mamzar', 'Al Buhairah Corniche',
    'Al Jubail', 'Al Fisht', 'Al Gharb', 'Al Sharq', 'Muwaileh', 'University City', 'Sharjah Industrial Area',
    'Al Ramla', 'Al Yarmook', 'Al Wahda', 'Al Qadisiya', 'Al Nasserya', 'Al Khezamia', 'Al Ghubaiba',
    'Sharjah Waterfront City', 'Tilal City', 'Aljada', 'Masaar', 'Al Rahmaniya', 'Al Suyoh',
    'Al Sajaa', 'Al Azra', 'Al Noaf', 'Al Qulayaa', 'Al Riqqa', 'Al Falaj', 'Al Mirgab',
    'Samnan', 'Bu Tina', 'Halwan', 'Al Gharayen', 'Al Brashi', 'Al Qarayen'
  ],
  Ajman: [
    'Ajman Downtown', 'Al Nuaimia', 'Al Rashidiya', 'Al Rumaila', 'Al Hamidiya', 'Al Mowaihat',
    'Al Jurf', 'Al Yasmeen', 'Emirates City', 'Ajman Uptown', 'Al Zahya', 'Al Rawda',
    'Al Bustan', 'Musheiref', 'Al Ittihad', 'Corniche Ajman', 'Al Helio', 'Al Sawan',
    'Al Nakheel', 'Al Owan', 'Al Amerah', 'Al Manama', 'Masfout'
  ],
  'Ras Al Khaimah': [
    'Al Hamra Village', 'Mina Al Arab', 'Al Marjan Island', 'Dafan Al Nakheel', 'Khuzam',
    'Al Nakheel', 'Al Dhait', 'Al Mamourah', 'Al Qurm', 'Al Turfa', 'Ras Al Khaimah City',
    'Al Mairid', 'Al Riffa', 'Al Rams', 'Ghalilah', 'Shaam', 'Masafi', 'Al Jazeera',
    'Hayat Island', 'Wynn Island', 'Al Qusaidat', 'Al Kharran', 'Sidroh', 'Al Hudaibah'
  ],
  Fujairah: [
    'Fujairah City', 'Al Faseel', 'Sakamkam', 'Mirbah', 'Al Aqah', 'Dibba Fujairah',
    'Masafi', 'Al Gurfa', 'Al Hayl', 'Qidfa', 'Sharm', 'Al Bidya', 'Corniche Fujairah',
    'Al Madhab', 'Al Ghurfa', 'Rugaylat', 'Zubarah'
  ],
  'Umm Al Quwain': [
    'Umm Al Quwain City', 'Al Salamah', 'Al Raas', 'Al Khor', 'Al Soor', 'Al Raudah',
    'Al Haditha', 'Al Maidan', 'Falaj Al Mualla', 'Al Abraq', 'Umm Al Quwain Marina',
    'Al Dar Al Baida', 'Al Neefah'
  ]
};

const CITIES = Object.keys(CITIES_AREAS);

// For backward compatibility - flat list
const COMMUNITIES = Object.values(CITIES_AREAS).flat();

const AMENITIES = [
  'Swimming Pool', 'Gym', 'Parking', 'Security', 'Concierge',
  'Beach Access', 'Marina View', 'City View', 'Balcony', 'Terrace',
  'Maid\'s Room', 'Study Room', 'Storage', 'Private Garden', 'BBQ Area'
];

export const PropertyCreatePage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [pricingAnalytics, setPricingAnalytics] = useState(null);
  
  // Drag and drop state for images
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);

  // Form State
  const [step1, setStep1] = useState({
    title: '',
    property_type: 'apartment',
    listing_type: 'buy',
    address: '',
    building_name: '',
    floor_number: '',
    unit_number: '',
    city: 'Dubai',
    community: '',
    bedrooms: 0,
    bathrooms: 0,
    area_sqft: '',
    asking_price: '',
    service_charge_per_sqft: '',
    parking_spaces: 1
  });

  const [step2, setStep2] = useState({
    title_deed_url: '',
    title_deed_number: '',
    registered_owner_name: '',
    ownership_confirmed: false
  });

  const [step3, setStep3] = useState({
    images: [],
    video_tour_url: '',
    virtual_tour_url: '',
    floor_plan_url: ''
  });

  const [step5, setStep5] = useState({
    listing_terms_accepted: false,
    commission_terms_accepted: false,
    availability_confirmed: false,
    ready_to_publish: false
  });

  const [description, setDescription] = useState('');
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [highlights, setHighlights] = useState(['', '', '']);

  const progress = ((currentStep + 1) / STEPS.length) * 100;
  
  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedImageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };
  
  const handleDragEnd = () => {
    setDraggedImageIndex(null);
    setDragOverIndex(null);
  };
  
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedImageIndex === null || draggedImageIndex === dropIndex) return;
    
    const newImages = [...step3.images];
    const draggedImage = newImages[draggedImageIndex];
    newImages.splice(draggedImageIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);
    
    setStep3(prev => ({ ...prev, images: newImages }));
    setDraggedImageIndex(null);
    setDragOverIndex(null);
  };

  const validateStep = (stepIndex) => {
    const newErrors = {};
    
    if (stepIndex === 0) {
      if (!step1.title) {
        newErrors.title = 'Title is required';
      } else if (step1.title.trim().split(/\s+/).length < 3) {
        newErrors.title = 'Title must be at least 3 words';
      }
      if (!step1.community) newErrors.community = 'Community is required';
      if (!step1.area_sqft || step1.area_sqft <= 0) newErrors.area_sqft = 'Area is required';
      if (!step1.asking_price || step1.asking_price <= 0) newErrors.asking_price = 'Price is required';
      if (!description) {
        newErrors.description = 'Description is required';
      } else if (description.trim().split(/\s+/).length < 20) {
        newErrors.description = 'Description must be at least 20 words';
      }
    }
    
    if (stepIndex === 1) {
      if (!step2.title_deed_url) newErrors.title_deed_url = 'Title deed document is required';
      if (!step2.title_deed_number) {
        newErrors.title_deed_number = 'Title deed number is required';
      } else if (!/^\d{3,4}-\d{4,6}-\d{1,4}$/.test(step2.title_deed_number.trim())) {
        newErrors.title_deed_number = 'Invalid format. Use Dubai format: XXX-XXXXXX-XX (e.g., 123-456789-01)';
      }
      if (!step2.registered_owner_name) newErrors.registered_owner_name = 'Owner name is required';
      if (!step2.ownership_confirmed) newErrors.ownership_confirmed = 'Ownership confirmation required';
    }
    
    if (stepIndex === 2) {
      if (step3.images.length < 9) {
        newErrors.images = `Minimum 9 photos required. You have ${step3.images.length}.`;
      } else if (step3.images.length > 15) {
        newErrors.images = `Maximum 15 photos allowed. You have ${step3.images.length}.`;
      }
    }
    
    if (stepIndex === 4) {
      if (!step5.listing_terms_accepted) newErrors.listing_terms = 'Must accept listing terms';
      if (!step5.availability_confirmed) newErrors.availability = 'Must confirm availability';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchPricingAnalytics = async () => {
    if (!step1.asking_price || !step1.area_sqft || !step1.community) return;
    
    setLoading(true);
    try {
      // Use the market analysis endpoint
      const res = await api.get('/zones/' + step1.community.toLowerCase().replace(' ', '_') + '/confidence');
      setPricingAnalytics({
        zone_confidence: res.data,
        price_per_sqft: Math.round(parseFloat(step1.asking_price) / parseFloat(step1.area_sqft)),
        area_benchmark: 3600, // Placeholder - would come from actual analysis
        price_positioning: parseFloat(step1.asking_price) / parseFloat(step1.area_sqft) < 3600 ? 'BELOW_MARKET' : 'AT_MARKET'
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      // Use default values
      setPricingAnalytics({
        zone_confidence: { confidence_index: { value: 70 }, liquidity_depth: 'Moderate' },
        price_per_sqft: Math.round(parseFloat(step1.asking_price) / parseFloat(step1.area_sqft)),
        area_benchmark: 3600,
        price_positioning: 'AT_MARKET'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;
    
    if (currentStep === 2) {
      // Fetch pricing analytics before step 4
      await fetchPricingAnalytics();
    }
    
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploadingImages(true);
    setErrors(prev => ({ ...prev, images: null }));
    
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('files', file);
    });
    
    try {
      const response = await api.post('/upload/images?category=property', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.uploaded) {
        const urls = response.data.uploaded
          .filter(r => r.url)
          .map(r => `${process.env.REACT_APP_BACKEND_URL}${r.url}`);
        setStep3(prev => ({ ...prev, images: [...prev.images, ...urls] }));
      }
    } catch (error) {
      console.error('Failed to upload images:', error);
      setErrors(prev => ({ ...prev, images: 'Failed to upload images. Please try again.' }));
    } finally {
      setUploadingImages(false);
    }
  };

  const handleTitleDeedUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/upload/image?category=document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.url) {
        setStep2(prev => ({ 
          ...prev, 
          title_deed_url: `${process.env.REACT_APP_BACKEND_URL}${response.data.url}` 
        }));
      }
    } catch (error) {
      console.error('Failed to upload title deed:', error);
      setErrors(prev => ({ ...prev, title_deed_url: 'Failed to upload document. Please try again.' }));
    }
  };

  const handleSubmit = async (asDraft = false) => {
    if (!asDraft && !validateStep(4)) return;
    
    setLoading(true);
    try {
      const payload = {
        step1: {
          ...step1,
          bedrooms: parseInt(step1.bedrooms) || 0,
          bathrooms: parseInt(step1.bathrooms) || 0,
          area_sqft: parseFloat(step1.area_sqft),
          asking_price: parseFloat(step1.asking_price),
          service_charge_per_sqft: step1.service_charge_per_sqft ? parseFloat(step1.service_charge_per_sqft) : null,
          parking_spaces: parseInt(step1.parking_spaces) || 0,
          floor_number: step1.floor_number ? parseInt(step1.floor_number) : null
        },
        step2,
        step3,
        step5: asDraft ? { ...step5, ready_to_publish: false } : { ...step5, ready_to_publish: true },
        description,
        amenities: selectedAmenities,
        highlights: highlights.filter(h => h.trim())
      };

      const res = await api.post('/seller/properties/create', payload);
      
      navigate('/seller/dashboard', { 
        state: { message: asDraft ? 'Property saved as draft' : 'Property submitted for review' }
      });
    } catch (error) {
      console.error('Failed to create property:', error);
      setErrors({ submit: error.response?.data?.detail || 'Failed to create property' });
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    const num = parseFloat(price);
    if (isNaN(num)) return 'AED 0';
    if (num >= 1000000) return `AED ${(num / 1000000).toFixed(1)}M`;
    return `AED ${num.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="property-create">
      {/* Header */}
      <header className="border-b border-white/10 bg-secondary/20">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/seller/dashboard')} className="btn-gold-metallic-outline">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </div>
          <h1 className="font-display text-2xl mb-2">Create Property Listing</h1>
          <p className="text-sm text-muted-foreground mb-4">
            Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].label}
          </p>
          <Progress value={progress} className="h-1" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Step Indicators */}
        <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
          {STEPS.map((step, idx) => (
            <div 
              key={step.id} 
              className={`
                flex items-center gap-2 px-3 py-2 rounded-full cursor-pointer whitespace-nowrap
                ${idx === currentStep ? 'bg-primary/20 text-primary' : idx < currentStep ? 'bg-green-600/20 text-green-400' : 'bg-secondary/30 text-muted-foreground'}
              `}
              onClick={() => idx <= currentStep && setCurrentStep(idx)}
            >
              {idx < currentStep ? <CheckCircle2 className="w-4 h-4" /> : <step.icon className="w-4 h-4" />}
              <span className="text-sm font-medium hidden md:inline">{step.label}</span>
            </div>
          ))}
        </div>

        {/* Step 1: Property Details */}
        {currentStep === 0 && (
          <Card className="glass-card border-white/10" data-testid="step-details">
            <CardHeader>
              <CardTitle>Property Details</CardTitle>
              <CardDescription>Basic information about your property</CardDescription>
              {Object.keys(errors).length > 0 && (
                <div className="mt-3 p-3 rounded bg-red-600/10 border border-red-600/30">
                  <p className="text-sm text-red-400 font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Please fix the following errors:
                  </p>
                  <ul className="text-xs text-red-400 mt-1 list-disc pl-5">
                    {Object.values(errors).map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Listing Title *</Label>
                <Input
                  placeholder="e.g., Stunning 2BR Apartment with Marina View"
                  value={step1.title}
                  onChange={(e) => setStep1(prev => ({ ...prev, title: e.target.value }))}
                  data-testid="title-input"
                  className={errors.title ? 'border-red-500' : ''}
                />
                {errors.title && <p className="text-xs text-red-400">{errors.title}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Property Type *</Label>
                  <Select value={step1.property_type} onValueChange={(val) => setStep1(prev => ({ ...prev, property_type: val }))}>
                    <SelectTrigger data-testid="property-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Select 
                    value={step1.city} 
                    onValueChange={(val) => setStep1(prev => ({ ...prev, city: val, community: '' }))}
                  >
                    <SelectTrigger data-testid="city-select">
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {CITIES.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Area/Community *</Label>
                <Select value={step1.community} onValueChange={(val) => setStep1(prev => ({ ...prev, community: val }))}>
                  <SelectTrigger data-testid="community-select">
                    <SelectValue placeholder={`Select area in ${step1.city}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {(CITIES_AREAS[step1.city] || []).map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.community && <p className="text-xs text-red-400">{errors.community}</p>}
              </div>

              <div className="space-y-2">
                <Label>Full Address</Label>
                <Input
                  placeholder="Building name, street, area"
                  value={step1.address}
                  onChange={(e) => setStep1(prev => ({ ...prev, address: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Building Name</Label>
                  <Input
                    placeholder="e.g., Marina Gate"
                    value={step1.building_name}
                    onChange={(e) => setStep1(prev => ({ ...prev, building_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Floor</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 15"
                    value={step1.floor_number}
                    onChange={(e) => setStep1(prev => ({ ...prev, floor_number: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit Number</Label>
                  <Input
                    placeholder="e.g., 1501"
                    value={step1.unit_number}
                    onChange={(e) => setStep1(prev => ({ ...prev, unit_number: e.target.value }))}
                  />
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Bed className="w-4 h-4" /> Bedrooms</Label>
                  <Select value={String(step1.bedrooms)} onValueChange={(val) => setStep1(prev => ({ ...prev, bedrooms: parseInt(val) }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3, 4, 5, 6, 7].map(n => (
                        <SelectItem key={n} value={String(n)}>{n === 0 ? 'Studio' : n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Bath className="w-4 h-4" /> Bathrooms</Label>
                  <Select value={String(step1.bathrooms)} onValueChange={(val) => setStep1(prev => ({ ...prev, bathrooms: parseInt(val) }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6].map(n => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Square className="w-4 h-4" /> Area (sqft) *</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 1500"
                    value={step1.area_sqft}
                    onChange={(e) => setStep1(prev => ({ ...prev, area_sqft: e.target.value }))}
                    data-testid="area-input"
                  />
                  {errors.area_sqft && <p className="text-xs text-red-400">{errors.area_sqft}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Parking</Label>
                  <Select value={String(step1.parking_spaces)} onValueChange={(val) => setStep1(prev => ({ ...prev, parking_spaces: parseInt(val) }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3, 4].map(n => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">Asking Price (AED) *</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 2500000"
                    value={step1.asking_price}
                    onChange={(e) => setStep1(prev => ({ ...prev, asking_price: e.target.value }))}
                    data-testid="price-input"
                    max={500000000}
                  />
                  {step1.asking_price && (
                    <p className="text-xs text-muted-foreground">{formatPrice(step1.asking_price)}</p>
                  )}
                  {errors.asking_price && <p className="text-xs text-red-400">{errors.asking_price}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Service Charge (AED/sqft/year)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 15"
                    value={step1.service_charge_per_sqft}
                    onChange={(e) => setStep1(prev => ({ ...prev, service_charge_per_sqft: e.target.value }))}
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  placeholder="Describe your property in detail (minimum 20 words)..."
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  data-testid="description-input"
                  className={errors.description ? 'border-red-500' : ''}
                />
                {description && (
                  <p className="text-xs text-muted-foreground">
                    {description.trim().split(/\s+/).length}/20 words minimum
                  </p>
                )}
                {errors.description && <p className="text-xs text-red-400">{errors.description}</p>}
              </div>

              <div className="space-y-2">
                <Label>Amenities</Label>
                <div className="grid grid-cols-3 gap-2">
                  {AMENITIES.map(amenity => (
                    <div key={amenity} className="flex items-center gap-2">
                      <Checkbox
                        id={amenity}
                        checked={selectedAmenities.includes(amenity)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedAmenities(prev => [...prev, amenity]);
                          } else {
                            setSelectedAmenities(prev => prev.filter(a => a !== amenity));
                          }
                        }}
                      />
                      <Label htmlFor={amenity} className="text-sm cursor-pointer">{amenity}</Label>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Ownership Verification */}
        {currentStep === 1 && (
          <Card className="glass-card border-white/10" data-testid="step-ownership">
            <CardHeader>
              <CardTitle>Ownership Verification</CardTitle>
              <CardDescription>Upload your title deed to verify property ownership</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 rounded bg-blue-600/10 border border-blue-600/30">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-400">Why we need this</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Title deed verification ensures you are the legitimate owner of this property. 
                      This document will be kept confidential and only used for verification purposes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Title Deed Document *</Label>
                <div 
                  className={`
                    border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                    ${step2.title_deed_url ? 'border-green-600/50 bg-green-600/5' : 'border-white/20 hover:border-primary/50'}
                  `}
                  onClick={() => document.getElementById('title-deed-upload').click()}
                >
                  {step2.title_deed_url ? (
                    <>
                      <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
                      <p className="font-medium text-green-400">Title Deed Uploaded</p>
                      <p className="text-sm text-muted-foreground mt-1">Click to replace</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="font-medium">Upload Title Deed</p>
                      <p className="text-sm text-muted-foreground mt-1">PDF, JPG, or PNG (max 10MB)</p>
                    </>
                  )}
                  <input
                    id="title-deed-upload"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => handleTitleDeedUpload(e.target.files[0])}
                  />
                </div>
                {errors.title_deed_url && <p className="text-xs text-red-400">{errors.title_deed_url}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title Deed Number *</Label>
                  <Input
                    placeholder="XXX-XXXXXX-XX (e.g., 123-456789-01)"
                    value={step2.title_deed_number}
                    onChange={(e) => setStep2(prev => ({ ...prev, title_deed_number: e.target.value }))}
                    data-testid="title-deed-number"
                  />
                  <p className="text-xs text-muted-foreground">
                    Dubai Land Department format: Plot-Parcel-Unit (e.g., 123-456789-01)
                  </p>
                  {errors.title_deed_number && <p className="text-xs text-red-400">{errors.title_deed_number}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Registered Owner Name *</Label>
                  <Input
                    placeholder="As shown on title deed"
                    value={step2.registered_owner_name}
                    onChange={(e) => setStep2(prev => ({ ...prev, registered_owner_name: e.target.value }))}
                    data-testid="owner-name"
                  />
                  {errors.registered_owner_name && <p className="text-xs text-red-400">{errors.registered_owner_name}</p>}
                </div>
              </div>

              <div className="p-4 rounded border border-white/10 bg-secondary/10">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="ownership"
                    checked={step2.ownership_confirmed}
                    onCheckedChange={(checked) => setStep2(prev => ({ ...prev, ownership_confirmed: checked }))}
                    data-testid="ownership-confirm"
                  />
                  <div className="flex-1">
                    <Label htmlFor="ownership" className="text-sm font-medium cursor-pointer">
                      I confirm that I am the legal owner of this property *
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      I declare that the title deed uploaded is authentic and I have the legal right 
                      to list and sell this property through Rashii.
                    </p>
                  </div>
                </div>
              </div>
              {errors.ownership_confirmed && <p className="text-xs text-red-400">{errors.ownership_confirmed}</p>}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Media Upload */}
        {currentStep === 2 && (
          <Card className="glass-card border-white/10" data-testid="step-media">
            <CardHeader>
              <CardTitle>Property Photos</CardTitle>
              <CardDescription>
                Upload high-quality photos of your property. Drag to reorder. First image becomes the cover.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Photo Requirements Banner */}
              <div className="p-4 rounded bg-blue-950/20 border border-blue-600/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Image className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-sm font-medium text-blue-400">Photo Requirements</p>
                      <p className="text-xs text-muted-foreground">Minimum 9 photos • Maximum 15 photos</p>
                    </div>
                  </div>
                  <Badge className={step3.images.length >= 9 && step3.images.length <= 15 
                    ? 'bg-green-600/20 text-green-400 border-green-600/30' 
                    : 'bg-gold-600/20 text-gold-400 border-gold-600/30'}>
                    {step3.images.length}/9-15
                  </Badge>
                </div>
              </div>
              
              <div 
                className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => document.getElementById('photo-upload').click()}
              >
                <Image className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="font-medium">Click to upload photos</p>
                <p className="text-sm text-muted-foreground mt-1">JPG or PNG, max 5MB each • Drag images to reorder</p>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files)}
                />
              </div>
              {errors.images && <p className="text-xs text-red-400">{errors.images}</p>}
              {uploadingImages && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  Uploading images...
                </div>
              )}

              {step3.images.length > 0 && (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{step3.images.length} photos uploaded</span>
                    <span className="text-xs text-primary">Drag to reorder • First image = Cover</span>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    {step3.images.map((img, idx) => (
                      <div 
                        key={img + idx} 
                        className={`relative aspect-square rounded overflow-hidden cursor-grab active:cursor-grabbing transition-all duration-200 ${
                          draggedImageIndex === idx ? 'opacity-50 scale-95' : ''
                        } ${dragOverIndex === idx ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, idx)}
                      >
                        <img 
                          src={img} 
                          alt={`Property photo ${idx + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23333" width="100" height="100"/><text fill="%23666" x="50%" y="50%" text-anchor="middle" dy=".3em">No Preview</text></svg>';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                        <Badge className={`absolute top-2 left-2 ${idx === 0 ? 'bg-primary text-primary-foreground' : 'bg-black/60'}`}>
                          {idx === 0 ? '★ Cover' : `#${idx + 1}`}
                        </Badge>
                        <button
                          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center text-xs transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setStep3(prev => ({ 
                              ...prev, 
                              images: prev.images.filter((_, i) => i !== idx) 
                            }));
                          }}
                        >
                          ×
                        </button>
                        {idx === 0 && (
                          <div className="absolute bottom-2 left-2 right-2 text-center">
                            <span className="text-xs bg-black/60 px-2 py-1 rounded text-white">Main listing photo</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              <Separator />

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Video Tour URL (Optional)</Label>
                  <Input
                    placeholder="https://youtube.com/..."
                    value={step3.video_tour_url}
                    onChange={(e) => setStep3(prev => ({ ...prev, video_tour_url: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Virtual Tour URL (Optional)</Label>
                  <Input
                    placeholder="https://..."
                    value={step3.virtual_tour_url}
                    onChange={(e) => setStep3(prev => ({ ...prev, virtual_tour_url: e.target.value }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Pricing Analytics */}
        {currentStep === 3 && (
          <Card className="glass-card border-white/10" data-testid="step-pricing">
            <CardHeader>
              <CardTitle>Pricing Analytics</CardTitle>
              <CardDescription>Rashii's market intelligence for your listing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                </div>
              ) : pricingAnalytics ? (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded bg-secondary/20 text-center">
                      <p className="text-xs text-muted-foreground mb-1">Your Price/sqft</p>
                      <p className="text-2xl font-display text-primary">
                        AED {pricingAnalytics.price_per_sqft?.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-4 rounded bg-secondary/20 text-center">
                      <p className="text-xs text-muted-foreground mb-1">Area Benchmark</p>
                      <p className="text-2xl font-display">
                        AED {pricingAnalytics.area_benchmark?.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-4 rounded bg-secondary/20 text-center">
                      <p className="text-xs text-muted-foreground mb-1">Position</p>
                      <Badge className={
                        pricingAnalytics.price_positioning === 'BELOW_MARKET' 
                          ? 'bg-green-600/20 text-green-400' 
                          : 'bg-gold-600/20 text-gold-400'
                      }>
                        {pricingAnalytics.price_positioning?.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>

                  {pricingAnalytics.zone_confidence && (
                    <div className="p-4 rounded border border-primary/30 bg-primary/5">
                      <h4 className="text-sm font-medium mb-3">Zone Confidence: {step1.community}</h4>
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-xs text-muted-foreground">Confidence</p>
                          <p className="text-lg font-display text-primary">
                            {pricingAnalytics.zone_confidence.confidence_index?.value || 70}%
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Liquidity</p>
                          <p className="text-sm font-medium">{pricingAnalytics.zone_confidence.liquidity_depth}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Stability</p>
                          <p className="text-sm font-medium">{pricingAnalytics.zone_confidence.capital_stability || 'Moderate'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Momentum</p>
                          <p className="text-sm font-medium">{pricingAnalytics.zone_confidence.momentum || 'Stable'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="p-4 rounded bg-blue-600/10 border border-blue-600/30">
                    <div className="flex items-start gap-3">
                      <BarChart3 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-400">Rashii Recommendation</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {pricingAnalytics.price_positioning === 'BELOW_MARKET'
                            ? 'Your asking price is competitive. This positioning may attract more serious buyers and reduce time on market.'
                            : 'Your asking price is at market level. Consider the zone liquidity when setting expectations for time to close.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-3 opacity-50" />
                  <p>Unable to load pricing analytics</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 5: Review & Submit */}
        {currentStep === 4 && (
          <Card className="glass-card border-white/10" data-testid="step-review">
            <CardHeader>
              <CardTitle>Review & Submit for Approval</CardTitle>
              <CardDescription>Your listing will be reviewed by our team before going live</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Important Notice */}
              <div className="p-4 rounded bg-gold-950/20 border border-gold-600/30">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-gold-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gold-400">Submission Notice</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Your property will be submitted for review. Our team will verify the details and approve 
                      it within 24-48 hours. You will receive an email notification once approved.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Summary */}
              <div className="p-4 rounded bg-secondary/20">
                <h4 className="font-medium mb-3">Listing Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Title</p>
                    <p className="font-medium">{step1.title}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Price</p>
                    <p className="font-medium text-primary">{formatPrice(step1.asking_price)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Location</p>
                    <p className="font-medium">{step1.community}, {step1.city}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Size</p>
                    <p className="font-medium">{step1.bedrooms} bed • {step1.bathrooms} bath • {step1.area_sqft} sqft</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Photos</p>
                    <p className="font-medium">{step3.images.length} uploaded {step3.images.length >= 9 ? '✓' : '(minimum 9 required)'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Description</p>
                    <p className="font-medium">{description.trim().split(/\s+/).length} words {description.trim().split(/\s+/).length >= 20 ? '✓' : '(minimum 20 required)'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Title Deed</p>
                    <p className="font-medium text-green-400">✓ Uploaded</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Terms */}
              <div className="space-y-4">
                <div className="p-4 rounded border border-white/10">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="terms"
                      checked={step5.listing_terms_accepted}
                      onCheckedChange={(checked) => setStep5(prev => ({ ...prev, listing_terms_accepted: checked }))}
                    />
                    <div className="flex-1">
                      <Label htmlFor="terms" className="text-sm font-medium cursor-pointer">
                        I accept the Rashii Listing Terms *
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Including accuracy of information, photo authenticity, and pricing commitment.
                      </p>
                    </div>
                  </div>
                </div>
                {errors.listing_terms && <p className="text-xs text-red-400">{errors.listing_terms}</p>}

                <div className="p-4 rounded border border-white/10">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="availability"
                      checked={step5.availability_confirmed}
                      onCheckedChange={(checked) => setStep5(prev => ({ ...prev, availability_confirmed: checked }))}
                    />
                    <div className="flex-1">
                      <Label htmlFor="availability" className="text-sm font-medium cursor-pointer">
                        Property is available for sale *
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        I confirm this property is currently available and not under contract with another party.
                      </p>
                    </div>
                  </div>
                </div>
                {errors.availability && <p className="text-xs text-red-400">{errors.availability}</p>}
              </div>

              {errors.submit && (
                <div className="p-3 rounded bg-red-600/10 border border-red-600/30 text-red-400 text-sm">
                  {errors.submit}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-6">
          <Button 
            variant="outline" 
            onClick={currentStep === 0 ? () => navigate('/seller/dashboard') : handleBack}
            className="btn-gold-metallic-outline"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> {currentStep === 0 ? 'Dashboard' : 'Back'}
          </Button>
          
          <div className="flex gap-3">
            {currentStep === 4 && (
              <Button variant="outline" onClick={() => handleSubmit(true)} disabled={loading} className="btn-gold-metallic-outline">
                Save as Draft
              </Button>
            )}
            {currentStep < 4 ? (
              <Button onClick={handleNext} disabled={loading} data-testid="next-btn" className="btn-gold-metallic">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={() => handleSubmit(false)} disabled={loading} data-testid="submit-btn" className="btn-gold-metallic">
                {loading ? 'Submitting...' : 'Submit for Approval'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyCreatePage;
